/**
 * The Fleet screen's controller and modal overlay (M6 plan P28).
 *
 * - **Controller** (`createFleetController`, Node-tested): holds the `FleetScreenState`, reads the fleet service's
 *   `fleetView()` on `refresh()`, runs `reduceFleet` for every action and carries out its effects: a bulk preset
 *   goes out as one `applyPreset` command with the eligible ships (then a refresh), `open` calls the row hook (the
 *   ship detail, P29) and `close` calls the owner's close.
 * - **Section** (`createFleetSection`, dashboard W3 card M2): the body of Fleet › Ships (U lands there). The
 *   dashboard shell owns the frame, Close, the hints, the modal and the keys; its focus inputs arrive through the
 *   `DashNavPort`: arrows move (←/→ between the list and the control bar, which replaces the old in-panel Tab),
 *   Enter/Space select or press, F opens the ship, PgUp/PgDn page. The pad goes through `input` (the owner's
 *   `stepPadNav`). While mounted it re-reads the fleet every `refreshS`.
 *
 * UI events only.
 */
import { useEffect, type ReactElement } from 'react'
import { logger } from '../../core/logger'
import type { FleetService } from '../../fleet-link/service'
import type { DashSectionComponent, DashSectionProps } from '../dashboard/registry'
import type { NavInput } from '../strategic/mount'
import { Fleet } from './Fleet'
import { fleetInputAction, useLive, useNavInput } from './sectionHost'
import {
  appliedNotice,
  createFleetScreenState,
  fleetRows,
  reduceFleet,
  type FleetAction,
  type FleetEffect,
  type FleetScreenConfig,
  type FleetScreenState,
  type NameLookup,
  type PresetInfo,
} from './fleetModel'

/** What the Fleet screen needs from the fleet service. */
export type FleetScreenService = Pick<FleetService, 'fleetView' | 'run'>

export interface FleetControllerDeps {
  service: FleetScreenService
  names: NameLookup
  config: FleetScreenConfig
  presets: readonly PresetInfo[]
  /** A row was opened (Enter, →, a name click): the ship detail hook (P29). */
  onOpenShip?: (shipId: string) => void
  onClose?: () => void
}

export interface FleetController {
  readonly state: FleetScreenState
  readonly cap: number | undefined
  readonly loading: boolean
  dispatch(a: FleetAction): void
  /** Re-reads the fleet view; resolves when the new rows are in. */
  refresh(): Promise<void>
  /** Resolves when the last bulk preset (and its refresh) finished; for tests. */
  settled(): Promise<void>
  onChange(fn: () => void): () => void
}

export function createFleetController(deps: FleetControllerDeps): FleetController {
  const ctx = { presets: deps.presets, list: deps.config.list }
  let state = createFleetScreenState()
  let cap: number | undefined
  let loading = true
  let pending: Promise<void> = Promise.resolve()
  const listeners = new Set<() => void>()

  function changed(): void {
    for (const fn of [...listeners]) fn()
  }

  function step(a: FleetAction): void {
    const r = reduceFleet(state, a, ctx)
    state = r.state
    changed()
    if (r.effect !== null) run(r.effect)
  }

  async function apply(e: Extract<FleetEffect, { kind: 'apply' }>): Promise<void> {
    const out = await deps.service.run({ type: 'applyPreset', shipIds: [...e.shipIds], preset: e.preset.id })
    if (out.ok) {
      step({ type: 'notice', text: appliedNotice(e.preset, e.shipIds.length, e.skipped) })
      await controller.refresh()
    } else {
      logger.warn('Fleet screen: preset refused', { preset: e.preset.id, error: out.error })
      step({ type: 'notice', text: `Could not set ${e.preset.name}: ${out.error}` })
    }
  }

  function run(e: FleetEffect): void {
    switch (e.kind) {
      case 'apply':
        pending = apply(e).catch((err: unknown) => {
          logger.warn('Fleet screen: preset failed', err)
          step({ type: 'notice', text: `Could not set ${e.preset.name}` })
        })
        return
      case 'open':
        deps.onOpenShip?.(e.id)
        return
      case 'close':
        deps.onClose?.()
        return
    }
  }

  const controller: FleetController = {
    get state(): FleetScreenState {
      return state
    },
    get cap(): number | undefined {
      return cap
    },
    get loading(): boolean {
      return loading
    },
    dispatch: step,
    async refresh(): Promise<void> {
      try {
        const view = await deps.service.fleetView()
        cap = view.cap
        loading = false
        step({ type: 'rows', rows: fleetRows(view, deps.names, deps.config, deps.presets) })
      } catch (err: unknown) {
        loading = false
        logger.warn('Fleet screen: could not read the fleet', err)
        step({ type: 'notice', text: 'Could not read the fleet' })
      }
    },
    settled: () => pending,
    onChange(fn: () => void): () => void {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
  }
  return controller
}

// ---------------------------------------------------------------------------------------------
// The dashboard section (Fleet › Ships; W3 card M2)
// ---------------------------------------------------------------------------------------------

export interface FleetSectionOptions {
  service: FleetScreenService
  names: NameLookup
  config: FleetScreenConfig
  presets: readonly PresetInfo[]
  /** The ship detail hook (P29); a row's open does nothing until it is set. */
  onOpenShip?: (shipId: string) => void
  /** True while a layer above (the ship detail, P29) has the keys; the section then leaves the shell's inputs alone. */
  blocked?: () => boolean
}

export interface FleetSectionHost {
  readonly controller: FleetController
  /** The section body (`registerDashSection`); the shell owns the frame, Close, hints and keys. */
  readonly component: DashSectionComponent
  /** True while the section is on screen. */
  readonly shown: boolean
  /** A pad input while shown (the owner's `stepPadNav`). */
  input(i: NavInput): void
  /** Re-reads the fleet view (after the ship detail saved an order). */
  refresh(): void
  /** Re-renders now (the ship detail opened or closed above it: `blocked` changed). */
  redraw(): void
}

export function createFleetSection(opts: FleetSectionOptions): FleetSectionHost {
  let shown = 0
  const redraws = new Set<() => void>()
  const controller = createFleetController({
    service: opts.service,
    names: opts.names,
    config: opts.config,
    presets: opts.presets,
    onOpenShip: (id) => opts.onOpenShip?.(id),
  })
  const subscribe = (fn: () => void): (() => void) => {
    const off = controller.onChange(fn)
    redraws.add(fn)
    return (): void => {
      off()
      redraws.delete(fn)
    }
  }
  const refresh = (): Promise<void> => controller.refresh()

  function FleetSection({ nav }: DashSectionProps): ReactElement {
    useLive(subscribe, refresh, opts.config.refreshS * 1000)
    useNavInput(nav, (i) => {
      if (opts.blocked?.() === true) return false
      const a = fleetInputAction(i)
      if (a === null) return false
      controller.dispatch(a)
      return true
    })
    useEffect(() => {
      shown++
      return (): void => {
        shown--
      }
    }, [])
    return (
      <Fleet
        state={controller.state}
        presets={opts.presets}
        config={opts.config}
        dispatch={controller.dispatch}
        cap={controller.cap}
        loading={controller.loading}
        covered={opts.blocked?.() === true}
      />
    )
  }

  return {
    controller,
    component: FleetSection,
    get shown(): boolean {
      return shown > 0
    },
    input(i: NavInput): void {
      if (shown > 0) controller.dispatch({ type: 'nav', input: i })
    },
    refresh(): void {
      if (shown > 0) void controller.refresh()
    },
    redraw(): void {
      for (const fn of [...redraws]) fn()
    },
  }
}
